/**
 * Google Apps Script Web App client.
 * All data operations go through a single deployed GAS Web App URL.
 */
const GAS_URL = process.env.REACT_APP_GAS_URL || '';

const CACHE_TTL_MS = {
  getDashboardStats: 30_000,
  getOrders: 20_000,
  getAnalytics: 45_000,
  getSalesAnalytics: 45_000,
  getOwners: 30_000,
  getOwnerFinance: 30_000,
  getInventory: 20_000,
  getProductsAdmin: 30_000,
  getStockBatches: 30_000,
  getMessages: 15_000,
  getReturns: 20_000,
  getPendingPaymentVerifications: 15_000,
  getSettings: 60_000,
};

const WRITE_ACTIONS = new Set([
  'dispatchOrder',
  'updateOrderStatus',
  'cancelOrder',
  'markOrderReturned',
  'adjustInventory',
  'addStockBatch',
  'createProduct',
  'deactivateProduct',
  'verifyPayment',
  'rejectPayment',
  'updateSettings',
  'updateOwnerInvestment',
  'recordWithdrawal',
  'updateWithdrawal',
  'deleteWithdrawal',
  'approveWithdrawal',
  'rejectWithdrawal',
  'markMessageRead',
]);

const LIST_ACTIONS = new Set([
  'getOrders',
  'getMessages',
  'getReturns',
  'getProductsAdmin',
  'getOwners',
  'getStockBatches',
  'getPendingPaymentVerifications',
]);

const responseCache = new Map();

function cacheKey(action, payload) {
  return `${action}:${JSON.stringify(payload || {})}`;
}

export function clearApiCache() {
  responseCache.clear();
}

/** GAS should return arrays for list endpoints; guard against stale/malformed responses. */
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

async function gasRequest(action, payload = {}) {
  if (!GAS_URL) {
    throw new Error('REACT_APP_GAS_URL is not configured. Set it in your .env file.');
  }

  const key = cacheKey(action, payload);
  const ttl = CACHE_TTL_MS[action];
  if (ttl) {
    const hit = responseCache.get(key);
    if (hit && Date.now() - hit.at < ttl) {
      return LIST_ACTIONS.has(action) ? asArray(hit.data) : hit.data;
    }
  }

  const res = await fetch(GAS_URL, {
    method: 'POST',
    mode: 'cors',
    redirect: 'follow',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ action, ...payload }),
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Invalid response from Google Apps Script');
  }

  if (data.error) {
    const msg = String(data.error);
    if (msg.includes('Unknown action')) {
      throw new Error(
        `${msg} Your deployed Google Apps Script is outdated. ` +
        'Open Apps Script → paste ALL 5 files (especially Code.gs + Handlers.gs) → ' +
        'Deploy → Manage deployments → Edit → New version → Deploy.'
      );
    }
    throw new Error(msg);
  }
  let result = data.data !== undefined ? data.data : data;
  if (LIST_ACTIONS.has(action)) {
    result = asArray(result);
  }

  if (WRITE_ACTIONS.has(action)) {
    clearApiCache();
  } else if (ttl) {
    responseCache.set(key, { data: result, at: Date.now() });
  }

  return result;
}

export const api = {
  getDashboard: () => gasRequest('getDashboardStats'),
  getOrders: (status) =>
    gasRequest('getOrders').then((orders) =>
      status ? orders.filter((o) => o.status === status) : orders
    ),
  getOrder: (id) => gasRequest('getOrderById', { orderId: id }),
  dispatchOrder: (id) => gasRequest('dispatchOrder', { orderId: id }),
  updateOrderStatus: (id, status) => gasRequest('updateOrderStatus', { orderId: id, status }),
  cancelOrder: (id) => gasRequest('cancelOrder', { orderId: id }),
  getReturns: () => gasRequest('getReturns'),
  markReturned: (body) => gasRequest('markOrderReturned', body),
  getInventory: () => gasRequest('getInventory'),
  adjustInventory: (body) => gasRequest('adjustInventory', body),
  getStockBatches: () => gasRequest('getStockBatches'),
  addStockBatch: (body) => gasRequest('addStockBatch', body),
  getProducts: () => gasRequest('getProductsAdmin'),
  addProduct: (body) => gasRequest('createProduct', body),
  deactivateProduct: (id) => gasRequest('deactivateProduct', { id }),
  getAnalytics: () => gasRequest('getAnalytics'),
  getSalesAnalytics: () => gasRequest('getSalesAnalytics'),
  getSettings: () => gasRequest('getSettings'),
  updateSettings: (body) => gasRequest('updateSettings', body),
  getOwners: () => gasRequest('getOwners'),
  getOwnerFinance: (name) => gasRequest('getOwnerFinance', { ownerName: name }),
  updateOwnerInvestment: (ownerName, investmentAmount) =>
    gasRequest('updateOwnerInvestment', { ownerName, investmentAmount }),
  recordWithdrawal: (body) => gasRequest('recordWithdrawal', body),
  updateWithdrawal: (id, body) => gasRequest('updateWithdrawal', { id, ...body }),
  deleteWithdrawal: (id) => gasRequest('deleteWithdrawal', { id }),
  approveWithdrawal: (id, approvedBy = 'Admin') =>
    gasRequest('approveWithdrawal', { id, approvedBy }),
  rejectWithdrawal: (id, approvedBy = 'Admin') =>
    gasRequest('rejectWithdrawal', { id, approvedBy }),
  getMessages: () => gasRequest('getMessages'),
  markMessageRead: (id) => gasRequest('markMessageRead', { id }),
  verifyPayment: (id) => gasRequest('verifyPayment', { orderId: id }),
  rejectPayment: (id) => gasRequest('rejectPayment', { orderId: id }),
  getPendingPaymentVerifications: () => gasRequest('getPendingPaymentVerifications'),
};

export const formatPKR = (n) => `Rs. ${Number(n || 0).toLocaleString('en-PK')}`;
